import React, { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Save, Bot, FileJson, ToggleLeft, ToggleRight, AlertCircle, Upload, Sparkles, Eye, Check, X, RefreshCw, ShieldCheck } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const SCHEMA_FIELDS = [
  {
    key: 'quick_fit_schema',
    title: 'Quick Fit — Input Schema',
    desc: 'What the AI collects from the player during the ~3-5 minute Quick Fit interview.',
    icon: FileJson,
    color: '#D4A55A',
  },
  {
    key: 'advanced_fit_schema',
    title: 'Advanced Fit — Input Schema',
    desc: 'What the AI collects from the player during the in-depth Advanced Fit interview.',
    icon: FileJson,
    color: '#4A6741',
  },
  {
    key: 'launch_monitor_schema',
    title: 'Launch Monitor Schema',
    desc: 'Column aliases, source detection, and parsing rules for simulator file uploads.',
    icon: Upload,
    color: '#4A6741',
  },
  {
    key: 'stage_1_fit_profile_schema',
    title: 'Stage 1 — Ideal Fit Profile Contract',
    desc: "The canonical shape Claude must return for a player's ideal fit profile, before any catalog is considered. You can add fields, refine enums/descriptions - but a few protected fields the pipeline depends on can't be removed.",
    icon: ShieldCheck,
    color: '#4A6741',
  },
  {
    key: 'stage_2_recommendation_schema',
    title: 'Stage 2 — Catalog Recommendation Contract',
    desc: 'The canonical shape Claude must return when matching the frozen fit profile against real inventory. A few protected fields the results page depends on can\'t be removed.',
    icon: ShieldCheck,
    color: '#4A6741',
  },
];

function SchemaFieldCard({ field, value, onChange, errors }) {
  const Icon = field.icon;
  return (
    <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]" data-testid={`${field.key}-card`}>
      <div className="flex items-center space-x-4 mb-4">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${field.color}1A` }}>
          <Icon className="h-6 w-6" style={{ color: field.color }} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#2C3E2D]">{field.title}</h3>
          <p className="text-[#5A6B5C] text-sm">{field.desc}</p>
        </div>
      </div>

      {errors && errors.length > 0 && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid={`${field.key}-errors`}>
          <p className="text-xs font-bold text-red-700 mb-1">Save blocked - fix these before saving:</p>
          <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <Label className="text-[#2C3E2D] text-sm mb-2 block">Schema Definition</Label>
      <Textarea
        value={value}
        onChange={(e) => onChange(field.key, e.target.value)}
        className="w-full min-h-[280px] bg-[#F5F3EF] border-[#E8E4DC] text-[#2C3E2D] font-mono text-sm"
        data-testid={`${field.key}-textarea`}
      />
      <p className="text-xs text-[#5A6B5C] mt-2">{value.length} characters</p>
    </div>
  );
}

export default function AIFittingConfig() {
  const [config, setConfig] = useState({
    fitting_playbook_text: '',
    quick_fit_schema: '',
    advanced_fit_schema: '',
    launch_monitor_schema: '',
    stage_1_fit_profile_schema: '',
    stage_2_recommendation_schema: '',
    matching_enabled: false
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // AI Playbook Editor state
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/admin/ai-fitting-config');
      setConfig({
        fitting_playbook_text: response.data.fitting_playbook_text || '',
        quick_fit_schema: response.data.quick_fit_schema || '',
        advanced_fit_schema: response.data.advanced_fit_schema || '',
        launch_monitor_schema: response.data.launch_monitor_schema || '',
        stage_1_fit_profile_schema: response.data.stage_1_fit_profile_schema || '',
        stage_2_recommendation_schema: response.data.stage_2_recommendation_schema || '',
        matching_enabled: response.data.matching_enabled || false
      });
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load Club Fitting Wizard configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFieldErrors({});
      await api.put('/admin/ai-fitting-config', config);
      toast.success('AI fitting configuration saved!');
      setHasChanges(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && detail.field && Array.isArray(detail.errors)) {
        setFieldErrors({ [detail.field]: detail.errors });
        const fieldTitle = SCHEMA_FIELDS.find(f => f.key === detail.field)?.title || detail.field;
        toast.error(`Save blocked: "${fieldTitle}" would break the pipeline - see details below`);
      } else {
        toast.error('Failed to save configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };
  
  // AI Playbook Editor functions
  const handleAiPreview = async () => {
    if (!aiInstruction.trim()) {
      toast.error('Please enter an instruction for the AI');
      return;
    }
    
    try {
      setAiProcessing(true);
      const response = await api.post('/admin/ai-fitting-config/update-playbook', {
        instruction: aiInstruction,
        preview_only: true
      });
      setAiPreview(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiProcessing(false);
    }
  };
  
  const handleAiApply = async () => {
    if (!aiPreview) return;
    
    try {
      setAiProcessing(true);
      await api.post('/admin/ai-fitting-config/update-playbook', {
        instruction: aiInstruction,
        preview_only: false
      });
      
      // Update local state with new playbook
      setConfig(prev => ({ ...prev, fitting_playbook_text: aiPreview.updated_playbook }));
      setAiPreview(null);
      setShowPreview(false);
      setAiInstruction('');
      setHasChanges(false);
      toast.success('Playbook updated successfully!');
    } catch (error) {
      toast.error('Failed to apply changes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiProcessing(false);
    }
  };
  
  const handleAiCancel = () => {
    setAiPreview(null);
    setShowPreview(false);
  };

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="ai-fitting-loading">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#4A6741] border-r-transparent"></div>
        <p className="mt-4 text-[#5A6B5C]">Loading Club Fitting Wizard...</p>
      </div>
    );
  }

  return (
    <div data-testid="ai-fitting-config">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
            AI – Club Fitting Wizard
          </h2>
          <p className="text-[#5A6B5C] mt-1">Configure your AI-powered club fitting assistant</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="bg-[#4A6741] text-white hover:bg-[#3A5733] disabled:opacity-50"
          data-testid="save-ai-config-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      {hasChanges && (
        <div className="bg-[#D4A55A]/10 border border-[#D4A55A]/30 rounded-lg p-4 mb-6 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-[#D4A55A]" />
          <span className="text-[#2C3E2D]">You have unsaved changes</span>
        </div>
      )}

      <div className="space-y-8">
        {/* Matching Enabled Toggle */}
        <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-[#4A6741]/10 rounded-lg flex items-center justify-center">
                <Bot className="h-6 w-6 text-[#4A6741]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2C3E2D]">Club Fitting Wizard</h3>
                <p className="text-[#5A6B5C] text-sm">Enable AI-powered club fitting recommendations</p>
              </div>
            </div>
            <button
              onClick={() => handleChange('matching_enabled', !config.matching_enabled)}
              className="focus:outline-none"
              data-testid="matching-enabled-toggle"
            >
              {config.matching_enabled ? (
                <ToggleRight className="h-10 w-10 text-[#4A6741]" />
              ) : (
                <ToggleLeft className="h-10 w-10 text-[#5A6B5C]" />
              )}
            </button>
          </div>
          
          <div className={`mt-4 p-3 rounded-lg ${config.matching_enabled ? 'bg-[#4A6741]/10 text-[#4A6741]' : 'bg-[#F5F3EF] text-[#5A6B5C]'}`}>
            <span className="text-sm font-medium">
              Status: {config.matching_enabled ? '✓ Club Fitting Wizard is enabled' : '○ Club Fitting Wizard is disabled'}
            </span>
          </div>
        </div>

        {/* AI Playbook Maintenance Agent */}
        <div className="bg-gradient-to-br from-[#4A6741]/5 to-[#D4A55A]/5 p-6 rounded-lg border border-[#4A6741]/20">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#4A6741] to-[#D4A55A] rounded-lg flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#2C3E2D]">AI Playbook Assistant</h3>
              <p className="text-[#5A6B5C] text-sm">Describe changes or issues and let AI update your playbook</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#2C3E2D] text-sm mb-2 block">What would you like to change?</Label>
              <Textarea
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder="Examples:
• Add guidance for fitting senior players with slower swing speeds
• The AI is asking too many questions about torque - simplify this section
• Include recommendations for players who slice the ball
• Remove the section about iron shafts, focus only on driver shafts
• Make the AI more conversational and less technical"
                className="w-full min-h-[120px] bg-white border-[#E8E4DC] text-[#2C3E2D]"
                data-testid="ai-instruction-input"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={handleAiPreview}
                disabled={aiProcessing || !aiInstruction.trim()}
                className="bg-[#4A6741] text-white hover:bg-[#3A5733] disabled:opacity-50"
                data-testid="ai-preview-btn"
              >
                {aiProcessing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Changes
                  </>
                )}
              </Button>
              <span className="text-xs text-[#5A6B5C]">
                AI will show you the changes before applying them
              </span>
            </div>
          </div>
          
          {/* AI Preview Modal */}
          {showPreview && aiPreview && (
            <div className="mt-6 border-t border-[#E8E4DC] pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-[#2C3E2D]">Preview Changes</h4>
                <button onClick={handleAiCancel} className="text-[#5A6B5C] hover:text-[#2C3E2D]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Changes Summary */}
              <div className="bg-[#D4A55A]/10 border border-[#D4A55A]/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-[#2C3E2D]">
                  <strong>Summary:</strong> {aiPreview.changes_summary}
                </p>
              </div>
              
              {/* Side by side comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-[#5A6B5C] text-xs mb-2 block">CURRENT PLAYBOOK</Label>
                  <div className="bg-[#F5F3EF] p-4 rounded-lg max-h-[300px] overflow-y-auto">
                    <pre className="text-xs text-[#5A6B5C] whitespace-pre-wrap font-mono">
                      {aiPreview.original_playbook.slice(0, 2000)}
                      {aiPreview.original_playbook.length > 2000 && '...'}
                    </pre>
                  </div>
                </div>
                <div>
                  <Label className="text-[#4A6741] text-xs mb-2 block">UPDATED PLAYBOOK</Label>
                  <div className="bg-[#4A6741]/5 p-4 rounded-lg max-h-[300px] overflow-y-auto border border-[#4A6741]/20">
                    <pre className="text-xs text-[#2C3E2D] whitespace-pre-wrap font-mono">
                      {aiPreview.updated_playbook.slice(0, 2000)}
                      {aiPreview.updated_playbook.length > 2000 && '...'}
                    </pre>
                  </div>
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleAiApply}
                  disabled={aiProcessing}
                  className="bg-[#4A6741] text-white hover:bg-[#3A5733]"
                  data-testid="ai-apply-btn"
                >
                  {aiProcessing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Apply Changes
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleAiCancel}
                  variant="outline"
                  className="border-[#E8E4DC] text-[#5A6B5C] hover:bg-[#F5F3EF]"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Fitting Playbook Text */}
        <div className="bg-white p-6 rounded-lg border border-[#E8E4DC]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-[#4A6741]/10 rounded-lg flex items-center justify-center">
                <FileJson className="h-6 w-6 text-[#4A6741]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2C3E2D]">Fitting Playbook</h3>
                <p className="text-[#5A6B5C] text-sm">Instructions and context for the AI fitting assistant</p>
              </div>
            </div>
            <span className="text-xs bg-[#F5F3EF] text-[#5A6B5C] px-3 py-1 rounded-full">
              Manual editing available
            </span>
          </div>
          
          <div>
            <Label className="text-[#2C3E2D] text-sm mb-2 block">Playbook Text</Label>
            <Textarea
              value={config.fitting_playbook_text}
              onChange={(e) => handleChange('fitting_playbook_text', e.target.value)}
              placeholder="Enter the fitting playbook instructions here...

Example:
- Ask about the player's swing speed
- Determine their current ball flight tendencies
- Consider their handicap and experience level
- Match shaft flex and weight to their profile
..."
              className="w-full min-h-[300px] bg-[#F5F3EF] border-[#E8E4DC] text-[#2C3E2D] font-mono text-sm"
              data-testid="fitting-playbook-textarea"
            />
            <p className="text-xs text-[#5A6B5C] mt-2">
              {config.fitting_playbook_text.length} characters
            </p>
          </div>
        </div>

        {/* 5 canonical AI Fitting schemas - the actual runtime contract */}
        {SCHEMA_FIELDS.map(field => (
          <SchemaFieldCard
            key={field.key}
            field={field}
            value={config[field.key]}
            onChange={handleChange}
            errors={fieldErrors[field.key]}
          />
        ))}

        {/* Save Button at bottom */}
        <div className="flex justify-end pt-6 border-t border-[#E8E4DC]">
          <Button 
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-[#4A6741] text-white hover:bg-[#3A5733] disabled:opacity-50 px-8"
            data-testid="save-ai-config-btn-bottom"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>
    </div>
  );
}
